import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Trophy, Send, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface StatItem {
  title: string;
  count: number;
  icon: React.ElementType;
  href: string;
  color: string;
}

interface QuickStatsGridProps {
  stats: {
    events: number;
    challenges: number;
    invitesSent: number;
    following: number;
  };
  isLoading?: boolean;
}

const QuickStatsGrid: React.FC<QuickStatsGridProps> = ({ stats, isLoading }) => {
  const items: StatItem[] = [
    {
      title: "My Events",
      count: stats.events,
      icon: Calendar,
      href: "/dashboard/events",
      color: "text-blue-500",
    },
    {
      title: "My Challenges",
      count: stats.challenges,
      icon: Trophy,
      href: "/dashboard/challenges",
      color: "text-amber-500",
    },
    {
      title: "Invites Sent",
      count: stats.invitesSent,
      icon: Send,
      href: "/dashboard/invites",
      color: "text-green-500",
    },
    {
      title: "Following",
      count: stats.following,
      icon: Eye,
      href: "/dashboard/following",
      color: "text-purple-500",
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-5 w-5 mb-2" />
              <Skeleton className="h-8 w-12 mb-1" />
              <Skeleton className="h-4 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {items.map((item) => (
        <Link key={item.title} to={item.href}>
          <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full">
            <CardContent className="p-4">
              <item.icon className={cn("h-5 w-5 mb-2", item.color)} />
              <div className="text-2xl font-bold text-foreground">
                {item.count}
              </div>
              <div className="text-xs text-muted-foreground">
                {item.title}
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
};

export default QuickStatsGrid;
